import React, { useMemo } from 'react'
import { TextField, FormControl, Tooltip, Icon, Typography } from '@mui/material'
import { InputAdornment } from '@mui/material'
import { withStyles } from '@mui/styles'

import { getEndAdornment } from './number-input.utils'
import { numberInputStyles } from './number-input.styles'

/**
 * Componente interno que formatea números con separadores de miles y decimales.
 * @private
 */
function NumberFormatCustom(props) {
  const NumberFormat = require('react-number-format')
  const { inputRef, onChange, maxValue = 0, ...other } = props

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        })
      }}
      thousandSeparator="."
      decimalSeparator=","
      isNumericString
      allowNegative={false}
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values
        return formattedValue === '' || !maxValue || floatValue <= maxValue
      }}
    />
  )
}

/**
 * @typedef {object} NumberInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {boolean} [readOnly=false] - Si es `true`, muestra el valor como texto de solo lectura.
 * @property {string | number} [value=''] - El valor numérico actual del input.
 * @property {number} [decimalScale] - Número máximo de decimales permitidos.
 * @property {boolean} [fixedDecimalScale=false] - Si es `true`, siempre muestra el número fijo de decimales.
 * @property {number} [maxValue=0] - Valor máximo permitido. Si es 0, no hay límite.
 * @property {string} [units] - Unidades que se muestran al final del valor (ej. 'kg', 'm²', '€').
 * @property {string} [title] - Título que se muestra encima del input.
 * @property {string} [tooltip] - Texto del tooltip de ayuda.
 * @property {string} name - Nombre del campo para el evento onChange.
 * @property {function(object): void} onChange - Callback que se ejecuta cuando el valor cambia.
 * @property {'small' | 'medium'} [fontSize] - Tamaño de la fuente para el título.
 */

/**
 * Componente de entrada numérica con formato automático y validación.
 * 
 * Proporciona un input especializado para números con:
 * - **Formato automático**: Separadores de miles (.) y decimales (,)
 * - **Validación de rango**: Límite máximo configurable
 * - **Decimales controlados**: Configuración de escala decimal
 * - **Unidades**: Soporte para mostrar unidades al final del valor
 * - **Modo solo lectura**: Visualización formateada del valor
 * - **Tooltip de ayuda**: Información contextual opcional
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [amount, setAmount] = useState(1500.50);
 * 
 *   return (
 *     <NumberInput
 *       name="amount"
 *       title="Importe"
 *       value={amount}
 *       onChange={(e) => setAmount(e.target.value)}
 *       decimalScale={2}
 *       maxValue={10000}
 *       units="€"
 *       tooltip="Importe máximo: 10.000€"
 *     />
 *   );
 * };
 * 
 * @param {NumberInputProps} props - Las props del componente NumberInput.
 */
export const NumberInput = withStyles(numberInputStyles)(({
  classes,
  readOnly = false,
  value = '',
  decimalScale = undefined,
  fixedDecimalScale = false,
  maxValue = 0,
  units = null,
  ...props
}) => {
  const typographyVariant = props.fontSize === 'small' ? '2' : '1'

  const InputProps = useMemo(() => {
    return {
      className: classes.input,
      ...props.InputProps,
      inputComponent: (inputComponentProps) => (
        <NumberFormatCustom {...{ decimalScale, maxValue, ...inputComponentProps }} />
      ),
      endAdornment: units ? (
        <InputAdornment position="end">{getEndAdornment(units)}</InputAdornment>
      ) : null,
    }
  }, [classes.input, props.InputProps, decimalScale, maxValue, units])

  const tooltip = useMemo(() => {
    return props.tooltip ? (
      <span style={{ fontSize: 16, marginLeft: 4 }}>
        <Tooltip
          placement="top"
          title={
            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
              {props.tooltip}
            </Typography>
          }
        >
          <Icon fontSize="inherit">help_outline</Icon>
        </Tooltip>
      </span>
    ) : null
  }, [props.tooltip])

  return (
    <FormControl className={classes.root}>
      {props.title ? (
        <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
          {props.title + (tooltip ? tooltip : '')}
        </Typography>
      ) : null}
      {readOnly ? (
        <Typography
          variant={`body${typographyVariant}`}
          color="textSecondary"
          style={{ whiteSpace: 'pre-line' }}
        >
          {value || value === 0
            ? value.toString().replace(/\./g, ',') + (units ? ' ' + units : '')
            : '--'}
        </Typography>
      ) : (
        <TextField
          {...props}
          variant="outlined"
          value={value}
          onChange={props.onChange}
          name={props.name}
          InputProps={InputProps}
        />
      )}
    </FormControl>
  )
})
